import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import "../styles/List.css";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const List = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [departments, setDepartments] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found.');
      }
      
      const response = await axios.get(`${API_BASE_URL}/api/user-creation/list-admin`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        setUsers(response.data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError(error.response?.data?.message || 'Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/departments-admin`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.data.success) {
        setDepartments(response.data.departments || []);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

const handleDeleteUser = async () => {
  if (!userToDelete) return;
  
  setDeleting(true);
  setDeleteError('');
  
  try {
    const token = localStorage.getItem('token');
    
    // CORRECTED: Use the correct endpoint path
    const response = await axios.delete(
      `${API_BASE_URL}/api/user-creation/${userToDelete.cs_user_id}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (response.data.success) {
      setSuccessMessage(`User "${userToDelete.cs_name}" deleted successfully`);
      setUsers(users.filter(user => user.cs_user_id !== userToDelete.cs_user_id));
      setShowDeleteModal(false);
      setUserToDelete(null);
      
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    }
  } catch (error) {
    console.error('Error deleting user:', error);
    
    // More detailed error handling
    if (error.response) {
      switch (error.response.status) {
        case 404:
          setDeleteError('User not found or endpoint does not exist');
          break;
        case 403:
          setDeleteError('Permission denied. Only admins can delete users.');
          break;
        case 401:
          setDeleteError('Session expired. Please login again.');
          localStorage.clear();
          window.location.href = '/login';
          break;
        default:
          setDeleteError(error.response?.data?.message || 'Failed to delete user. Please try again.');
      }
    } else if (error.request) {
      setDeleteError('Network error. Please check your internet connection.');
    } else {
      setDeleteError('An error occurred. Please try again.');
    }
  } finally {
    setDeleting(false);
  }
};

  const openDeleteModal = (user, e) => {
    e.stopPropagation();
    setUserToDelete(user);
    setShowDeleteModal(true);
    setDeleteError('');
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.cs_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.cs_user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.cs_user_phone?.includes(searchTerm) ||
      user.cs_user_department?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDepartment = 
      filterDepartment === 'all' || 
      user.cs_user_department === filterDepartment;
    
    return matchesSearch && matchesDepartment;
  });

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setShowDetailModal(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPhone = (phone) => {
    if (!phone) return 'N/A';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
    }
    return phone;
  };

  const handleRefresh = () => {
    fetchUsers();
    fetchDepartments();
    setSuccessMessage('');
    setError('');
  };

  const handleDepartmentChange = (selectedOption) => {
    setFilterDepartment(selectedOption ? selectedOption.value : 'all');
  };

  const departmentOptions = [
    { value: 'all', label: 'All Departments' },
    ...departments.map(dept => ({ value: dept, label: dept }))
  ];

  if (loading) {
    return (
      <div className="list-container">
        <div className="list-content loading-state">
          <div className="spinner-large"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <div className="header-main">
          <h1 className="list-title">User Directory</h1>
          <p className="list-subtitle">
            Total Users: <span className="count">{users.length}</span> | 
            Showing: <span className="count">{filteredUsers.length}</span>
          </p>
        </div>
        <button 
          className="refresh-btn"
          onClick={handleRefresh}
          title="Refresh users list"
        >
          Refresh
        </button>
      </div>

      <div className="list-content">
        {successMessage && (
          <div className="success-message">
            <div className="success-content">
              <span className="success-icon">✓</span>
              <div className="success-text">{successMessage}</div>
            </div>
          </div>
        )}

        {error && (
          <div className="error-message">
            <div className="error-content">
              <span className="error-icon">!</span>
              <div className="error-text">{error}</div>
            </div>
          </div>
        )}

        <div className="filters-container">
          <div className="search-box">
            <div className="form-group">
              <label className="form-label">Search Users</label>
              <div className="search-input-wrapper">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                  placeholder="Search by name, email, phone, or department..."
                />
                {searchTerm && (
                  <button 
                    className="clear-search"
                    onClick={() => setSearchTerm('')}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>
          
          <div className="filter-box">
            <div className="form-group">
              <label className="form-label">Filter by Department</label>
              <Select
                value={departmentOptions.find(option => option.value === filterDepartment)}
                onChange={handleDepartmentChange}
                options={departmentOptions}
                placeholder="Select Department"
                classNamePrefix="react-select"
                menuPlacement="auto"
                menuPosition="fixed"
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    padding: '12px 16px',
                    border: state.isFocused ? '2px solid #4dabf7' : '2px solid #e9ecef',
                    borderRadius: '8px',
                    fontSize: '15px',
                    background: 'white',
                    boxShadow: state.isFocused ? '0 0 0 3px rgba(77, 171, 247, 0.1)' : 'none',
                    '&:hover': { borderColor: '#4dabf7' },
                    minHeight: '48px'
                  }),
                  menu: (provided) => ({
                    ...provided,
                    marginTop: '4px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                    border: '1px solid #e9ecef',
                    zIndex: 1000,
                    position: 'fixed',
                    width: '220px'
                  }),
                  menuList: (provided) => ({
                    ...provided,
                    maxHeight: '250px',
                    padding: '4px'
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    padding: '12px 16px',
                    fontSize: '14px',
                    backgroundColor: state.isSelected ? '#4dabf7' : state.isFocused ? '#f8f9fa' : 'white',
                    color: state.isSelected ? 'white' : '#495057',
                    '&:hover': {
                      backgroundColor: state.isSelected ? '#4dabf7' : '#f8f9fa'
                    }
                  }),
                  placeholder: (provided) => ({
                    ...provided,
                    color: '#6c757d'
                  }),
                  dropdownIndicator: (provided) => ({
                    ...provided,
                    color: '#6c757d',
                    '&:hover': {
                      color: '#4dabf7'
                    }
                  })
                }}
              />
            </div>
          </div>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="empty-state">
            <h3>No Users Found</h3>
            <p>
              {searchTerm || filterDepartment !== 'all' 
                ? 'Try changing your search criteria' 
                : 'No users have been created yet'}
            </p>
            {(searchTerm || filterDepartment !== 'all') && (
              <button 
                className="reset-filters-btn"
                onClick={() => {
                  setSearchTerm('');
                  setFilterDepartment('all');
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <div className="users-grid">
            {filteredUsers.map((user, index) => (
              <div 
                key={user.cs_user_id || index} 
                className="user-card"
              >
                <div className="user-header">
                  <div 
                    className="user-avatar"
                    style={{
                      background: `linear-gradient(135deg, #4dabf7 0%, #339af0 100%)`
                    }}
                  >
                    {user.cs_name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <h3 className="user-name">
                    {user.cs_name || 'N/A'}
                  </h3>
                </div>

                <div className="user-details-simple">
                  <div className="detail-row">
                    <span className="detail-label">Department:</span>
                    <span className="detail-value">{user.cs_user_department || 'N/A'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Phone:</span>
                    <span className="detail-value">{formatPhone(user.cs_user_phone)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Created:</span>
                    <span className="detail-value">{formatDate(user.cs_created_at)}</span>
                  </div>
                </div>

                <div className="card-footer">
                  <button 
                    className="view-details-btn"
                    onClick={() => handleUserClick(user)}
                  >
                    View Details
                  </button>
                  
                  {user.cs_user_level > 1 && (
                    <button 
                      className="delete-user-btn"
                      onClick={(e) => openDeleteModal(user, e)}
                      title="Delete User"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showDetailModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h3>User Details</h3>
            </div>

            <div className="dialog-body">
              <div className="user-details-modal">
                <div className="modal-detail-item">
                  <span className="modal-detail-label">Name:</span>
                  <span className="modal-detail-value">{selectedUser.cs_name || 'N/A'}</span>
                </div>
                <div className="modal-detail-item">
                  <span className="modal-detail-label">Email:</span>
                  <span className="modal-detail-value">{selectedUser.cs_user_email || 'N/A'}</span>
                </div>
                <div className="modal-detail-item">
                  <span className="modal-detail-label">Phone:</span>
                  <span className="modal-detail-value">{formatPhone(selectedUser.cs_user_phone)}</span>
                </div>
                <div className="modal-detail-item">
                  <span className="modal-detail-label">Department:</span>
                  <span className="modal-detail-value">{selectedUser.cs_user_department || 'N/A'}</span>
                </div>
                <div className="modal-detail-item">
                  <span className="modal-detail-label">Created by:</span>
                  <span className="modal-detail-value">
                    {selectedUser.created_by || 'System'}
                  </span>
                </div>
                <div className="modal-detail-item">
                  <span className="modal-detail-label">Created At:</span>
                  <span className="modal-detail-value">
                    {formatDate(selectedUser.cs_created_at)}
                  </span>
                </div>
              </div>
            </div>

            <div className="dialog-footer">
              <button
                onClick={() => setShowDetailModal(false)}
                className="close-modal-btn"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && userToDelete && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-dialog delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header warning">
              <h3>Delete User</h3>
            </div>

            <div className="dialog-body">
              <p className="delete-warning-text">
                Are you sure you want to delete this user? This action cannot be undone.
              </p>
              
              <div className="user-to-delete-info">
                <div className="delete-user-detail">
                  <span className="delete-label">Name:</span>
                  <span className="delete-value">{userToDelete.cs_name}</span>
                </div>
                <div className="delete-user-detail">
                  <span className="delete-label">Email:</span>
                  <span className="delete-value">{userToDelete.cs_user_email}</span>
                </div>
                <div className="delete-user-detail">
                  <span className="delete-label">Department:</span>
                  <span className="delete-value">{userToDelete.cs_user_department}</span>
                </div>
                <div className="delete-user-detail">
                  <span className="delete-label">Created:</span>
                  <span className="delete-value">{formatDate(userToDelete.cs_created_at)}</span>
                </div>
              </div>

              {deleteError && (
                <div className="error-message" style={{ marginTop: '15px' }}>
                  <div className="error-content">
                    <span className="error-icon">!</span>
                    <div className="error-text">{deleteError}</div>
                  </div>
                </div>
              )}
            </div>

            <div className="dialog-footer">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setUserToDelete(null);
                  setDeleteError('');
                }}
                className="cancel-delete-btn"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                className="confirm-delete-btn"
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <span className="spinner"></span>
                    Deleting...
                  </>
                ) : (
                  'Delete User'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default List;